import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import { Plus, Trash } from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { Textarea } from '@/components/ui-kit/textarea';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { Label } from '@/components/ui-kit/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui-kit/select';
import { Card, CardContent } from '@/components/ui-kit/card';
import { Question, QUESTION_FORMATS, QuestionFormatType } from '../../types/survey-form.types';

export interface QuestionEditorProps {
  questions: Question[];
  onChange: (questions: Question[]) => void;
}

const defaultsForFormat = (formatType: QuestionFormatType): Partial<Question> => {
  switch (formatType) {
    case 'multiple-choice':
    case 'checkboxes':
      return {
        Options: [
          { Label: 'Option 1', Value: 'option-1' },
          { Label: 'Option 2', Value: 'option-2' },
        ],
      };
    case 'rating':
    case 'nps':
      return { MinRating: formatType === 'nps' ? 0 : 1, MaxRating: formatType === 'nps' ? 10 : 5 };
    case 'likert-scale':
      return {
        LikertLabels: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'],
        LikertStatements: ['Statement 1'],
      };
    default:
      return {};
  }
};

export const QuestionEditor = ({ questions, onChange }: QuestionEditorProps) => {
  const { t } = useTranslation();

  const updateQuestion = (index: number, patch: Partial<Question>) => {
    onChange(questions.map((question, i) => (i === index ? { ...question, ...patch } : question)));
  };

  const addQuestion = () => {
    const formatType: QuestionFormatType = 'short-text';
    onChange([
      ...questions,
      {
        QuestionId: uuidv4(),
        FormatType: formatType,
        Question: '',
        Position: questions.length,
        IsRequired: true,
        HasComment: false,
        ...defaultsForFormat(formatType),
      },
    ]);
  };

  const removeQuestion = (index: number) => {
    onChange(questions.filter((_, i) => i !== index).map((question, i) => ({ ...question, Position: i })));
  };

  const moveQuestion = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= questions.length) return;
    const next = [...questions];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next.map((question, i) => ({ ...question, Position: i })));
  };

  const updateOption = (qIndex: number, oIndex: number, label: string) => {
    const question = questions[qIndex];
    const options = (question.Options ?? []).map((option, i) =>
      i === oIndex ? { Label: label, Value: label.toLowerCase().replace(/\s+/g, '-') || option.Value } : option
    );
    updateQuestion(qIndex, { Options: options });
  };

  const addOption = (qIndex: number) => {
    const question = questions[qIndex];
    const options = [...(question.Options ?? []), { Label: `Option ${(question.Options?.length ?? 0) + 1}`, Value: uuidv4() }];
    updateQuestion(qIndex, { Options: options });
  };

  const removeOption = (qIndex: number, oIndex: number) => {
    const question = questions[qIndex];
    updateQuestion(qIndex, { Options: (question.Options ?? []).filter((_, i) => i !== oIndex) });
  };

  return (
    <div className="flex flex-col gap-4">
      {questions.map((question, index) => (
        <Card key={question.QuestionId}>
          <CardContent className="flex flex-col gap-4 pt-6">
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-start">
              <div className="min-w-0 flex-1 space-y-3">
                <Input
                  placeholder={t('QUESTION')}
                  value={question.Question}
                  onChange={(e) => updateQuestion(index, { Question: e.target.value })}
                />
                <Textarea
                  rows={2}
                  placeholder={t('QUESTION_DESCRIPTION')}
                  value={question.QuestionDescription ?? ''}
                  onChange={(e) => updateQuestion(index, { QuestionDescription: e.target.value })}
                />
              </div>
              <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 sm:flex-col sm:items-stretch">
                <Button type="button" variant="outline" size="sm" onClick={() => moveQuestion(index, -1)}>
                  {t('MOVE_UP')}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => moveQuestion(index, 1)}>
                  {t('MOVE_DOWN')}
                </Button>
                <Button type="button" variant="ghost" size="icon-sm" onClick={() => removeQuestion(index)}>
                  <Trash className="size-4" />
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <div className="w-full sm:w-48">
                <Select
                  value={question.FormatType}
                  onValueChange={(value: QuestionFormatType) =>
                    updateQuestion(index, { FormatType: value, ...defaultsForFormat(value) })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {QUESTION_FORMATS.map((format) => (
                      <SelectItem key={format.key} value={format.key}>
                        {t(format.title)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={question.IsRequired}
                  onCheckedChange={(checked) => updateQuestion(index, { IsRequired: Boolean(checked) })}
                />
                {t('REQUIRED')}
              </label>

              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={question.HasComment}
                  onCheckedChange={(checked) => updateQuestion(index, { HasComment: Boolean(checked) })}
                />
                {t('ALLOW_COMMENT')}
              </label>
            </div>

            {(question.FormatType === 'multiple-choice' || question.FormatType === 'checkboxes') && (
              <div className="space-y-2">
                <Label>{t('OPTIONS')}</Label>
                {(question.Options ?? []).map((option, oIndex) => (
                  <div key={oIndex} className="flex items-center gap-2">
                    <Input
                      value={option.Label}
                      onChange={(e) => updateOption(index, oIndex, e.target.value)}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-lg"
                      onClick={() => removeOption(index, oIndex)}
                    >
                      <Trash className="size-4" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => addOption(index)}>
                  <Plus className="size-4" />
                  {t('ADD_OPTION')}
                </Button>
              </div>
            )}

            {(question.FormatType === 'rating' || question.FormatType === 'nps') && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>{t('MIN_RATING')}</Label>
                  <Input
                    type="number"
                    value={question.MinRating ?? 0}
                    onChange={(e) => updateQuestion(index, { MinRating: e.target.valueAsNumber })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>{t('MAX_RATING')}</Label>
                  <Input
                    type="number"
                    value={question.MaxRating ?? 5}
                    onChange={(e) => updateQuestion(index, { MaxRating: e.target.valueAsNumber })}
                  />
                </div>
              </div>
            )}

            {question.FormatType === 'likert-scale' && (
              <div className="space-y-2">
                <Label>{t('LIKERT_LABELS')}</Label>
                <Input
                  value={(question.LikertLabels ?? []).join(', ')}
                  onChange={(e) =>
                    updateQuestion(index, {
                      LikertLabels: e.target.value.split(',').map((label) => label.trim()).filter(Boolean),
                    })
                  }
                />
                <Label>{t('LIKERT_STATEMENTS')}</Label>
                <Input
                  value={(question.LikertStatements ?? []).join(', ')}
                  onChange={(e) =>
                    updateQuestion(index, {
                      LikertStatements: e.target.value
                        .split(',')
                        .map((statement) => statement.trim())
                        .filter(Boolean),
                    })
                  }
                />
              </div>
            )}
          </CardContent>
        </Card>
      ))}

      <Button type="button" variant="outline" onClick={addQuestion}>
        <Plus className="size-4" />
        {t('ADD_QUESTION')}
      </Button>
    </div>
  );
};
